import React, { useEffect, useMemo, useState } from 'react';
import { getOperadores } from '../../admin/Bitacoras/integracion';
import { getExcesosVelocidad } from './integracion';
import { formatearFechaGuion } from '../../../utilidades/formateos';
import { useExcelExport } from '../../../utilidades/customHooks/useExcelExport';
import Filtros from './partes/Filtros';
import Tabla from './partes/Tabla';
import Indicadores from './partes/Indicadores';
import GestionRiesgo from './partes/GestionRiesgo';
import AnalisisClientes from './partes/AnalisisClientes';
import RutasCriticas from './partes/RutasCriticas';
import GraficaDias from './partes/GraficaDias';
import './ExcesoVelocidad.scss';

const getPrimerDiaMes = () => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth(), 1);
};

const getUltimoDiaMes = () => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
};

const COLUMNAS_EXCEL = [
    { field: 'ID', header: 'ID' },
    { field: 'FECHA', header: 'FECHA' },
    { field: 'ID_UNIDAD', header: 'UNIDAD' },
    { field: 'OPERADOR', header: 'OPERADOR' },
    { field: 'TIPO_UNIDAD', header: 'TIPO UNIDAD' },
    { field: 'CLIENTE', header: 'CLIENTE' },
    { field: 'RUTA', header: 'RUTA' },
    { field: 'VELOCIDAD', header: 'VELOCIDAD (km/h)' },
    { field: 'DESCRIPCION', header: 'DESCRIPCIÓN' },
    { field: 'LATITUD', header: 'LATITUD' },
    { field: 'LONGITUD', header: 'LONGITUD' },
];

const ExcesoVelocidad = () => {
    const [filtros, setFiltros] = useState({
        desde: getPrimerDiaMes(),
        hasta: getUltimoDiaMes(),
        operador: null,
    });
    const [datos, setDatos] = useState([]);
    const [operadores, setOperadores] = useState([]);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [clienteFiltro, setClienteFiltro] = useState(null);
    const [tipoUnidadFiltro, setTipoUnidadFiltro] = useState(null);
    const [loading, setLoading] = useState(false);
    const { exportarAExcel } = useExcelExport();

    // Tipos de unidad únicos derivados de la data (sin endpoint)
    const opcionesTipoUnidad = useMemo(() => {
        const unicos = [...new Set(datos.map((d) => d.TIPO_UNIDAD).filter(Boolean))];
        return unicos.sort().map((t) => ({ label: t, value: t }));
    }, [datos]);

    // Clientes únicos derivados de la data (sin endpoint)
    const opcionesClientes = useMemo(() => {
        const unicos = [...new Set(datos.map((d) => d.CLIENTE).filter(Boolean))];
        return unicos.sort().map((c) => ({ label: c, value: c }));
    }, [datos]);

    // Data filtrada por cliente y tipo de unidad
    const datosFiltrados = useMemo(() => {
        let result = datos;
        if (clienteFiltro) result = result.filter((d) => d.CLIENTE === clienteFiltro);
        if (tipoUnidadFiltro) result = result.filter((d) => d.TIPO_UNIDAD === tipoUnidadFiltro);
        return result;
    }, [datos, clienteFiltro, tipoUnidadFiltro]);

    useEffect(() => {
        getOperadores(setOperadores);
        buscar();
    }, []);

    const buscar = async () => {
        setLoading(true);
        try {
            const params = {
                fecha_inicio: formatearFechaGuion(filtros.desde),
                fecha_fin: formatearFechaGuion(filtros.hasta),
                id_chofer: filtros.operador?.code ?? null,
            };
            const response = await getExcesosVelocidad(params);
            setDatos(response ?? []);
        } catch (error) {
            console.error('Error fetching Excesos de Velocidad:', error);
        } finally {
            setLoading(false);
        }
    };

    const limpiar = () => {
        setFiltros({ desde: getPrimerDiaMes(), hasta: getUltimoDiaMes(), operador: null });
        setGlobalFilterValue('');
        setClienteFiltro(null);
        setTipoUnidadFiltro(null);
        setDatos([]);
    };

    return (
        <div className="ev-container">

            <Filtros
                filtros={filtros}
                setFiltros={setFiltros}
                operadores={operadores}
                opcionesClientes={opcionesClientes}
                clienteFiltro={clienteFiltro}
                setClienteFiltro={setClienteFiltro}
                opcionesTipoUnidad={opcionesTipoUnidad}
                tipoUnidadFiltro={tipoUnidadFiltro}
                setTipoUnidadFiltro={setTipoUnidadFiltro}
                buscar={buscar}
                limpiar={limpiar}
                loading={loading}
                datosFiltrados={datosFiltrados}
                columnasExcel={COLUMNAS_EXCEL}
                exportarAExcel={exportarAExcel}
            />

            {/* DASHBOARD — se muestra solo cuando hay datos */}
            {datosFiltrados.length > 0 && (
                <>
                    {/* Leyenda TVP — se muestra una sola vez */}
                    <div className="ev-tvp-banner">
                        <i className="pi pi-info-circle ev-tvp-banner-icon" />
                        <span>
                            Las secciones <strong>TVP</strong> muestran:&nbsp;
                            <span className="ev-tvp-pill ev-tvp-pill-t">T Total</span>
                            <span className="ev-tvp-pill ev-tvp-pill-v">V Vel. máxima</span>
                            <span className="ev-tvp-pill ev-tvp-pill-p">P Promedio</span>
                        </span>
                    </div>
                    {/* Fila 1: Indicadores generales TVP */}
                    <Indicadores datos={datosFiltrados} />

                    {/* Fila 2: Gestión de riesgo + Gráfica por día */}
                    <div className="ev-dashboard-row">
                        <GestionRiesgo datos={datosFiltrados} />
                        <GraficaDias datos={datosFiltrados} />
                    </div>

                    {/* Fila 3: Análisis de clientes + Rutas críticas */}
                    <div className="ev-dashboard-row">
                        <AnalisisClientes datos={datosFiltrados} />
                        <RutasCriticas datos={datosFiltrados} />
                    </div>
                </>
            )}

            {/* Tabla detalle (siempre visible para mostrar estado vacío) */}
            <Tabla
                datosFiltrados={datosFiltrados}
                loading={loading}
                globalFilterValue={globalFilterValue}
                setGlobalFilterValue={setGlobalFilterValue}
            />

        </div>
    );
};

export default ExcesoVelocidad;